import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import {
  getAppointmentPaymentRefundContext,
  getPaymentRefundDetail,
  issueAppointmentPaymentRefund,
  searchPaymentRefunds,
  syncPaymentRefund,
} from '../../services/paymentOperationsService';

const ROWS_PER_PAGE = 25;

const REFUND_REASONS = ['RequestedByCustomer', 'Duplicate', 'Fraudulent'];

const formatUtcDateTime = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const formatAmount = (minorUnits, currency) => {
  if (minorUnits == null) {
    return '—';
  }

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(minorUnits / 100);
};

const PaymentRefundsSection = ({ refreshToken, onFeedback }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRefundId, setSelectedRefundId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [appointmentIdInput, setAppointmentIdInput] = useState('');
  const [refundContext, setRefundContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [refundForm, setRefundForm] = useState({
    amountMinorUnits: '',
    reason: 'RequestedByCustomer',
    internalNote: '',
  });

  const loadRefunds = useCallback(async () => {
    setLoading(true);

    try {
      const result = await searchPaymentRefunds({
        pageIndex: page - 1,
        itemsPerPage: ROWS_PER_PAGE,
      });
      setRows(result.data);
      setTotal(result.totalItems);
    } catch (err) {
      console.error('Failed to load payment refunds:', err);
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.error.loading'),
      });
    } finally {
      setLoading(false);
    }
  }, [onFeedback, page, t]);

  useEffect(() => {
    loadRefunds();
  }, [loadRefunds, refreshToken]);

  const openDetail = async (row) => {
    setSelectedRefundId(row.id);
    setDetail(null);
    setDetailLoading(true);

    try {
      const refundDetail = await getPaymentRefundDetail(row.id);
      setDetail(refundDetail);
    } catch (err) {
      console.error('Failed to load refund detail:', err);
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.error.loading'),
      });
      setSelectedRefundId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedRefundId(null);
    setDetail(null);
  };

  const loadRefundContext = async () => {
    if (!appointmentIdInput.trim()) {
      return;
    }

    setContextLoading(true);
    setRefundContext(null);

    try {
      const context = await getAppointmentPaymentRefundContext(appointmentIdInput.trim());
      setRefundContext(context);
      setRefundForm((current) => ({
        ...current,
        amountMinorUnits: context.remainingRefundableMinorUnits
          ? String(context.remainingRefundableMinorUnits / 100)
          : '',
      }));
    } catch (err) {
      console.error('Failed to load refund context:', err);
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.refunds.loadContextError'),
      });
    } finally {
      setContextLoading(false);
    }
  };

  const handleIssueRefund = async () => {
    if (!refundContext?.canIssueRefund) {
      return;
    }

    setActionLoading(true);

    try {
      const amountInput = refundForm.amountMinorUnits.trim();
      const amountMinorUnits = amountInput
        ? Math.round(Number.parseFloat(amountInput) * 100)
        : null;

      await issueAppointmentPaymentRefund(refundContext.appointmentId, {
        amountMinorUnits,
        reason: refundForm.reason,
        internalNote: refundForm.internalNote || null,
      });

      onFeedback?.({
        severity: 'success',
        message: t('paymentOperations.refunds.issueSuccess'),
      });

      setIssueDialogOpen(false);
      setAppointmentIdInput('');
      setRefundContext(null);
      setRefundForm({ amountMinorUnits: '', reason: 'RequestedByCustomer', internalNote: '' });
      await loadRefunds();
    } catch (err) {
      console.error('Failed to issue refund:', err);
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.refunds.issueError'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncRefund = async () => {
    if (!detail) {
      return;
    }

    setActionLoading(true);

    try {
      const updated = await syncPaymentRefund(detail.id);
      setDetail(updated);
      onFeedback?.({
        severity: 'success',
        message: t('paymentOperations.refunds.syncSuccess'),
      });
      await loadRefunds();
    } catch (err) {
      console.error('Failed to sync refund:', err);
      onFeedback?.({
        severity: 'error',
        message: err?.response?.data?.message || t('paymentOperations.refunds.syncError'),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'storeName',
      header: t('paymentOperations.refunds.columns.store'),
      render: (row) => row.storeName || '—',
    },
    {
      field: 'amountMinorUnits',
      header: t('paymentOperations.refunds.columns.amount'),
      render: (row) => formatAmount(row.amountMinorUnits, row.currency),
    },
    {
      field: 'status',
      header: t('paymentOperations.refunds.columns.status'),
      render: (row) => (
        <Chip size="small" label={row.status} />
      ),
    },
    {
      field: 'reason',
      header: t('paymentOperations.refunds.columns.reason'),
      render: (row) => t(`paymentOperations.refunds.reasons.${row.reason}`, row.reason),
    },
    {
      field: 'createdAt',
      header: t('paymentOperations.refunds.columns.createdAt'),
      render: (row) => formatUtcDateTime(row.createdAt),
    },
    {
      field: 'providerRefundId',
      header: t('paymentOperations.refunds.columns.refundId'),
      render: (row) => row.providerRefundId,
    },
  ], [t]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={() => setIssueDialogOpen(true)}>
          {t('paymentOperations.refunds.issueRefund')}
        </Button>
      </Box>

      <DataTable
        data={rows}
        loading={loading}
        columns={columns}
        searchable={false}
        filterable={false}
        pagination
        currentPage={page}
        totalCount={total}
        rowsPerPage={ROWS_PER_PAGE}
        onPageChange={setPage}
        actionMenuItems={[{
          label: t('paymentOperations.refunds.view'),
          tooltip: t('paymentOperations.refunds.view'),
          onClick: (row) => openDetail(row),
        }]}
        emptyMessage={t('paymentOperations.empty')}
        noResultsMessage={t('paymentOperations.empty')}
        onRefresh={loadRefunds}
      />

      <Dialog open={issueDialogOpen} onClose={() => setIssueDialogOpen(false)} maxWidth="sm" fullWidth disableScrollLock>
        <DialogTitle>{t('paymentOperations.refunds.issueTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
            <TextField
              fullWidth
              label={t('paymentOperations.refunds.appointmentId')}
              value={appointmentIdInput}
              onChange={(event) => setAppointmentIdInput(event.target.value)}
            />
            <Button variant="outlined" onClick={loadRefundContext} disabled={contextLoading}>
              {contextLoading ? <CircularProgress size={20} /> : t('paymentOperations.refunds.loadContext')}
            </Button>
          </Box>

          {refundContext ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!refundContext.canIssueRefund ? (
                <Alert severity="warning">{t('paymentOperations.refunds.cannotRefund')}</Alert>
              ) : null}
              <Typography variant="body2">
                {`${t('paymentOperations.refunds.context.store')}: ${refundContext.storeName}`}
              </Typography>
              <Typography variant="body2">
                {`${t('paymentOperations.refunds.context.chargeAmount')}: ${formatAmount(refundContext.chargeAmountMinorUnits, refundContext.currency)}`}
              </Typography>
              <Typography variant="body2">
                {`${t('paymentOperations.refunds.context.refundedAmount')}: ${formatAmount(refundContext.refundedAmountMinorUnits, refundContext.currency)}`}
              </Typography>
              <Typography variant="body2">
                {`${t('paymentOperations.refunds.context.remainingAmount')}: ${formatAmount(refundContext.remainingRefundableMinorUnits, refundContext.currency)}`}
              </Typography>
              <TextField
                label={t('paymentOperations.refunds.amount')}
                value={refundForm.amountMinorUnits}
                onChange={(event) => setRefundForm((current) => ({ ...current, amountMinorUnits: event.target.value }))}
                helperText={t('paymentOperations.refunds.amountHelp')}
                disabled={!refundContext.canIssueRefund}
              />
              <FormControl fullWidth disabled={!refundContext.canIssueRefund}>
                <InputLabel>{t('paymentOperations.refunds.reason')}</InputLabel>
                <Select
                  label={t('paymentOperations.refunds.reason')}
                  value={refundForm.reason}
                  onChange={(event) => setRefundForm((current) => ({ ...current, reason: event.target.value }))}
                >
                  {REFUND_REASONS.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {t(`paymentOperations.refunds.reasons.${reason}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('paymentOperations.refunds.internalNote')}
                value={refundForm.internalNote}
                onChange={(event) => setRefundForm((current) => ({ ...current, internalNote: event.target.value }))}
                multiline
                minRows={2}
                disabled={!refundContext.canIssueRefund}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialogOpen(false)}>{t('common.close')}</Button>
          <Button
            variant="contained"
            onClick={handleIssueRefund}
            disabled={actionLoading || !refundContext?.canIssueRefund}
          >
            {actionLoading ? t('paymentOperations.refunds.issuing') : t('paymentOperations.refunds.confirmIssue')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedRefundId)} onClose={closeDetail} maxWidth="md" fullWidth disableScrollLock>
        <DialogTitle>{t('paymentOperations.refunds.detailTitle')}</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : null}
          {detail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              <Typography variant="body2">
                <strong>{t('paymentOperations.refunds.columns.refundId')}:</strong> {detail.providerRefundId}
              </Typography>
              <Typography variant="body2">
                <strong>{t('paymentOperations.refunds.appointmentId')}:</strong> {detail.appointmentId}
              </Typography>
              <Typography variant="body2">
                <strong>{t('paymentOperations.refunds.columns.amount')}:</strong> {formatAmount(detail.amountMinorUnits, detail.currency)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('paymentOperations.refunds.columns.status')}:</strong> {detail.status}
              </Typography>
              <Typography variant="body2">
                <strong>{t('paymentOperations.refunds.columns.reason')}:</strong> {t(`paymentOperations.refunds.reasons.${detail.reason}`, detail.reason)}
              </Typography>
              {detail.internalNote ? (
                <Typography variant="body2">
                  <strong>{t('paymentOperations.refunds.internalNote')}:</strong> {detail.internalNote}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>{t('common.close')}</Button>
          <Button onClick={handleSyncRefund} disabled={actionLoading || !detail}>
            {t('paymentOperations.refunds.sync')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentRefundsSection;
